import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { first } from 'lodash-es';
import { forkJoin, Observable, of, timer } from 'rxjs';
import { catchError, filter, map, mapTo, mergeMap, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { ExtractionRuleActions } from './extraction-rule.actions';
import { ExtractionRuleSelectors } from './extraction-rule.selectors';
import { ViewerCommand } from '../../features/document-viewer/models/document-viewer.models';
import {
  FAIL_NOTIFICATION_CONFIGURATION,
  SUCCESS_NOTIFICATION_CONFIGURATION,
} from '../../features/shared/constants/notification-configurations';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import { WebSocketEntities, WebSocketReloadEvent } from '../../features/shared/models/web-socket/web-socket.models';
import { DatapointValuePipe } from '../../features/shared/pipes/datapoint-value.pipe';
import { FormulaValueFormatterPipe } from '../../features/shared/pipes/formula-value-formatter.pipe';
import { ParagraphService } from '../../features/shared/services/paragraph.service';
import { createWebSocketWithWarningEffect } from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import { createNotificationEffect, PushNotificationRecord } from '../../features/shared/utils/create-notification-effect';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { DatapointRule, DatapointRuleType } from '../../features/unified-datapoint/models/datapoint-rule.model';
import { DatapointRuleService } from '../../features/unified-datapoint/services/datapoint-rule.service';
import { DatapointService } from '../../features/unified-datapoint/services/datapoint.service';
import { ParagraphSet } from '../../generated/graphql-models';
import { AppActions } from '../app/app.actions';
import { DatapointFieldActions } from '../datapoint-field/datapoint-field.actions';
import { DatapointFieldListActions } from '../datapoint-field-list/datapoint-field-list.actions';
import { DealFilterActions } from '../deal-filter/deal-filter.actions';
import { DocumentViewerActions } from '../document-viewer/document-viewer.actions';
import { ExtractionRuleListActions } from '../extraction-rule-list/extraction-rule-list.actions';
import { RootState } from '../root-state.model';

@Injectable()
export class ExtractionRuleEffects {
  private readonly datapointService = inject(DatapointService);
  private readonly datapointRuleService = inject(DatapointRuleService);
  private readonly paragraphService = inject(ParagraphService);
  private readonly webSocketService = inject(WebSocketService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);

  private readonly POLLING_INTERVAL: number = 5_000;

  private readonly notifications: PushNotificationRecord = {
    [ExtractionRuleActions.ImportRulesSuccess.type]: { message: 'Rules imported', isSuccess: true },
    [ExtractionRuleActions.StartPolling.type]: { message: 'Extraction started', isSuccess: true },
    [ExtractionRuleActions.UpdateDatapointRuleSuccess.type]: {
      message: 'Datapoint saved',
      isSuccess: true,
    },
    [ExtractionRuleActions.UpdateRuleBaseSuccess.type]: {
      message: 'Datapoint saved',
      isSuccess: true,
    },
    [ExtractionRuleActions.RunExperimentSuccess.type]: {
      message: 'Ground Truth calculated',
      isSuccess: true,
    },
    [ExtractionRuleActions.UpdateRuleBaseFail.type]: {
      message: 'Failed to save the datapoint',
      isSuccess: false,
    },
    [ExtractionRuleActions.UpdateDatapointRuleFail.type]: {
      message: 'Failed to save the extraction',
      isSuccess: false,
    },
    [ExtractionRuleActions.LoadExtractionProgressFail.type]: {
      message: 'Failed to load the datapoints',
      isSuccess: false,
    },
    [ExtractionRuleListActions.CloneModelSuccess.type]: {
      message: 'Datapoint cloned',
      isSuccess: true,
    },
    [DatapointFieldListActions.CloneModelSuccess.type]: {
      message: 'Datapoint cloned',
      isSuccess: true,
    },
    [DatapointFieldListActions.CloneModelFail.type]: {
      message: 'Failed to clone the datapoint',
      isSuccess: false,
    },
    [ExtractionRuleListActions.CloneModelFail.type]: {
      message: 'Failed to clone the datapoint',
      isSuccess: false,
    },
    [DatapointFieldActions.LoadDatapointFieldFail.type]: {
      message: 'Failed to load the datapoint',
      isSuccess: false,
    },
    [ExtractionRuleActions.LoadDatapointRuleFail.type]: {
      message: 'Failed to load the datapoint',
      isSuccess: false,
    },
    [ExtractionRuleActions.StartPollingFailed.type]: {
      message: 'Failed to start the extraction',
      isSuccess: false,
    },
    [ExtractionRuleActions.RunExperimentFail.type]: {
      message: 'Failed to start the Ground Truth calculation',
      isSuccess: false,
    },
  };

  redirectOnUnauthorized$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldActions.LoadDatapointFieldFail, ExtractionRuleActions.LoadDatapointRuleFail),
      map(() => AppActions.Unauthorized()),
    ),
  );

  runAfterSaveEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.UpdateDatapointRuleSuccess),
      withLatestFrom(this.store),
      filter(([action, store]) => !!action.run),
      map(([action, store]) =>
        ExtractionRuleActions.RunRule({
          transactionIds: 'all',
        }),
      ),
    ),
  );

  stopPollingScenariosEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.LoadExtractionProgressFail, DealFilterActions.UseFilter),
      withLatestFrom(this.store),
      filter(([action, store]) => !!store.extractionRule.isPolling),
      map(() => ExtractionRuleActions.StopPolling({ notification: true })),
    ),
  );

  stopPollingSaveAndRunScenariosEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.UpdateDatapointRuleSuccess),
      withLatestFrom(this.store),
      filter(([action, store]) => !!store.extractionRule.isPolling),
      map(() => ExtractionRuleActions.StopPolling({ notification: true })),
    ),
  );

  loadDatapointsPollingEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.StartPolling),
      switchMap((action) =>
        timer(0, this.POLLING_INTERVAL).pipe(
          map(() => action),
          takeUntil(this.actions$.pipe(ofType(ExtractionRuleActions.StopPolling))),
        ),
      ),
      withLatestFrom(this.store),
      map(([action, store]) =>
        ExtractionRuleActions.LoadExtractionProgress({
          datapointHash: store.extractionRule.rule?.ruleHash,
          transactions: action.transactionIds,
        }),
      ),
    ),
  );

  loadExtractionProgressEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.LoadExtractionProgress),
      switchMap((action) =>
        this.datapointService.getExtractionProgress(action.datapointHash, action.transactions).pipe(
          map((extractionProgress) =>
            ExtractionRuleActions.LoadExtractionProgressSuccess({
              extractionProgress,
              transactions: action.transactions,
            }),
          ),
          reportEffectError(ExtractionRuleActions.LoadExtractionProgressFail),
        ),
      ),
    ),
  );

  runExperimentEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.RunExperiment),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.datapointRuleService.runExperiment(store.extractionRule.rule).pipe(
          map((rule) => ExtractionRuleActions.RunExperimentSuccess({ experiment: rule.experiment })),
          reportEffectError(ExtractionRuleActions.RunExperimentFail),
        ),
      ),
    ),
  );

  importRules$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.ImportRules),
      map((action) =>
        ExtractionRuleActions.ImportRulesSuccess({
          rules: this.datapointRuleService.importRules(action.json),
        }),
      ),
      catchError((error) =>
        of(
          AppActions.PushNotification({
            message: 'Failed to import rules',
            config: FAIL_NOTIFICATION_CONFIGURATION,
          }),
        ),
      ),
    ),
  );

  LoadResultEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.LoadResult),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        const isFormula = store?.extractionRule?.rule?.ruleType === DatapointRuleType.Formula;

        if (!isFormula && (!action.transaction.documents?.length || !action.transaction.confidence)) {
          return of(ExtractionRuleActions.LoadResultSuccess({ datapoints: [], result: [] }));
        }

        return this.datapointService.getDatapointsByTransaction(store.extractionRule.rule.ruleHash, action.transaction.transactionId).pipe(
          switchMap((datapoints) => {
            switch (store?.extractionRule?.rule?.ruleType) {
              case DatapointRuleType.Section:
              case DatapointRuleType.DefinedTerm:
              case DatapointRuleType.Paragraph:
                return forkJoin([of(datapoints), this.paragraphService.getParagraphsByDatapoints(datapoints)]);
              case DatapointRuleType.LLM:
              case DatapointRuleType.Formula: {
                const datapoint = first(datapoints);

                const datapointValuePipe = new DatapointValuePipe();
                const formulaFormatterPipe = new FormulaValueFormatterPipe();
                return forkJoin([
                  of(datapoints),
                  of(
                    datapoint?.isParagraph
                      ? ParagraphService.paragraphSetToViewModels(datapoint.value as ParagraphSet)
                      : formulaFormatterPipe.transform(datapointValuePipe.transform(datapoint)),
                  ),
                ]);
              }
              default:
                return forkJoin([of(datapoints), of([])]);
            }
          }),
          tap(([datapoints]) => {
            const datapoint = first(datapoints);
            const sourceReferences = datapoint?.sourceReferences;
            if (sourceReferences?.length) {
              const ref = first(sourceReferences);
              this.store.dispatch(
                DocumentViewerActions.SendCommand({
                  message: {
                    commandType: ViewerCommand.SourceReference,
                    value: [ref],
                  },
                  documentId: ref.documentShortName,
                }),
              );
            }
          }),
          map(([datapoints, result]) => ExtractionRuleActions.LoadResultSuccess({ datapoints, result })),
          reportEffectError(ExtractionRuleActions.LoadResultFail),
        );
      }),
    ),
  );

  loadExtractionProgressSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.LoadExtractionProgressSuccess),
      withLatestFrom(this.store),
      filter(
        ([action, store]) =>
          !!store.extractionRule.isPolling &&
          ((ids) => action.transactions.every((t) => ids.has(t)))(new Set(action.extractionProgress.map((d) => d.libraryTransactionId))),
      ),
      map(() => ExtractionRuleActions.StopPolling({ notification: true })),
    ),
  );

  checkHashEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.CheckHash),
      withLatestFrom(this.store),
      map(([action, store]) => ({
        hash: store.extractionRule.rule?.ruleHash,
        id: store.extractionRule.rule?.id,
      })),
      filter(({ id, hash }) => !!hash && !!id),
      switchMap(({ hash, id }) =>
        this.datapointRuleService.getById(id).pipe(
          filter((rule) => rule.ruleHash !== hash),
          map((rule) => ExtractionRuleActions.LoadDatapointRuleSuccess({ rule })),
        ),
      ),
    ),
  );

  loadDatapointRuleEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.LoadDatapointRule),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.datapointRuleService.getById(action.id).pipe(
          map((rule) => ExtractionRuleActions.LoadDatapointRuleSuccess({ rule })),
          reportEffectError(ExtractionRuleActions.LoadDatapointRuleFail),
        ),
      ),
    ),
  );

  updateDatapointRuleEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.UpdateDatapointRule),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.datapointRuleService
          .update(
            {
              id: action.id,
              active: action.datapointRule.active,
              addEmbedding: action.datapointRule.addEmbedding,
              showInvalidatedDatapoints: action.datapointRule.showInvalidatedDatapoints,
              ruleType: store.extractionRule.rule.ruleType,
              rule: action.datapointRule.rule,
            } as DatapointRule,
            true,
            false,
            false,
          )
          .pipe(
            map((res) => {
              return ExtractionRuleActions.UpdateDatapointRuleSuccess({
                rule: {
                  ...store.extractionRule.rule,
                  active: action.datapointRule.active,
                  addEmbedding: action.datapointRule.addEmbedding,
                  showInvalidatedDatapoints: action.datapointRule.showInvalidatedDatapoints,
                  rule: action.datapointRule.rule,
                  id: res.id,
                  ruleHash: res.ruleHash,
                },
                run: action.run,
              });
            }),
            reportEffectError(ExtractionRuleActions.UpdateDatapointRuleFail),
          ),
      ),
    ),
  );

  templateModalClosedEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.TemplateModalClosed),
      map((action) =>
        action.modalId === 'edit-extraction-rule' && !!action.data ? { data: action.data, additionalInfo: action.additionalInfo } : {},
      ),
      filter(({ data }) => !!data),
      map(({ data, additionalInfo }) =>
        ExtractionRuleActions.UpdateRuleBase({
          id: data.id || additionalInfo?.rule?.id,
          rule: {
            active: additionalInfo?.rule?.ruleType === DatapointRuleType.LLM ? undefined : additionalInfo?.rule?.active,
            addEmbedding: additionalInfo?.rule?.addEmbedding,
            id: data.id || additionalInfo?.rule?.id,
            description: data.description,
            displayName: data.displayName,
            displayShortName: data.displayShortName,
            shared: data.shared,
            sharedForEdit: data.sharedForEdit,
            template: data.template,
            hidden: data.hidden,
            templateTenantAccess: data.templateTenantAccess,
            assetShortNames: data.assetShortNames,
            myEntity: additionalInfo?.rule?.myEntity,
            canShare: additionalInfo?.rule?.canShare,
            canPublish: additionalInfo?.rule?.canPublish,
            rule: additionalInfo?.rule?.rule,
            ruleType: additionalInfo?.rule?.ruleType,
            referenceNumber: data.referenceNumber,
            presentationType: data.presentationType,
            verificationType: data.verificationType,
            groundTruthDatapointField: data.groundTruthDatapointField,
            aiContribution: data.aiContribution,
            integrationIdentifier: data.integrationIdentifier,
            details: data.details,
            stats: data.stats,
            meta: data.meta,
            ttlSeconds: data.ttlSeconds,
          } as DatapointRule,
        }),
      ),
    ),
  );

  updateRuleBaseEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.UpdateRuleBase),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.datapointRuleService.update(action.rule, true, !!action.rule.canShare, !!action.rule.canPublish).pipe(
          map((rule) => {
            return ExtractionRuleActions.UpdateRuleBaseSuccess({
              id: action.id,
              rule: {
                ...rule,
                nameWithReference: rule.nameWithReference,
                ruleHash: rule.ruleHash,
              },
            });
          }),
          reportEffectError(ExtractionRuleActions.UpdateRuleBaseFail),
        ),
      ),
    ),
  );

  StopPollingEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.StopPolling),
      filter((action) => !!action.notification),
      map((action) =>
        AppActions.PushNotification({
          message: 'Extraction finished',
          config: SUCCESS_NOTIFICATION_CONFIGURATION,
        }),
      ),
    ),
  );

  runRuleEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.RunRule),
      withLatestFrom(this.store, this.store.select(ExtractionRuleSelectors.unpreviewedTransactions)),
      switchMap(([action, store, transactions]) => {
        const requestedTransactionIds = action.transactionIds === 'all' ? transactions.map((t) => t.transactionId) : action.transactionIds;
        const force = store.extractionRule.errorsOnly;

        return (
          force
            ? this.datapointService
                .deleteDatapointsByHash(store.extractionRule.rule.ruleHash, requestedTransactionIds)
                .pipe(mapTo(requestedTransactionIds))
            : of(requestedTransactionIds)
        ).pipe(
          mergeMap((transactionIds) =>
            this.datapointRuleService
              .runRule(store.extractionRule.rule as DatapointRule, transactionIds, 'normal')
              .pipe(map(() => transactionIds)),
          ),

          map((transactionIds) =>
            ExtractionRuleActions.StartPolling({
              transactionIds: transactionIds.length ? transactionIds : [],
            }),
          ),
          reportEffectError(ExtractionRuleActions.StartPollingFailed),
        );
      }),
    ),
  );

  deleteDatapointsEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractionRuleActions.DeleteDatapoints),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.datapointService.deleteDatapointsByHash(action.ruleHash, action.transactionIds).pipe(
          map(() => ExtractionRuleActions.DeleteDatapointsSuccess()),
          reportEffectError(ExtractionRuleActions.LoadExtractionProgressFail),
        ),
      ),
    ),
  );

  notFoundRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ExtractionRuleActions.LoadDatapointRuleFail),
        map(() => this.router.navigate(['/', SemerisRoutes.datapoint.root])),
      ),
    { dispatch: false },
  );

  notificationEffect$: Observable<Action> = createNotificationEffect(this.actions$, this.notifications);

  wsEffect$: Observable<Action> = createWebSocketWithWarningEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.Datapoint,
    (state) =>
      window.location.pathname.includes(SemerisRoutes.datapoint.field.root)
        ? DatapointFieldActions.LoadDatapointField({ id: state.datapointField?.datapointField?.id })
        : ExtractionRuleActions.LoadDatapointRule({ id: state.extractionRule?.rule?.id }),
    WebSocketReloadEvent.DatapointReload,
  );
}
